import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faCheck, faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Card, Grid, Typography } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import ClpButton from "components/Button"
import IconForButton from "components/ButtonIcon"
import PaperBox from "components/PaperBox"
import { RouterPrompt } from "components/RouterPrompt"
import theme from "css/theme/light"
import { Formik, FormikValues } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import { t as globalT } from "i18next"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import * as Yup from "yup"
import { IFormAreaProps } from "../FormArea/formarea.interface"
import FormComponents from "../FormComponents/FormComponents"
import { IFormFieldProps } from "../FormField/formfield.interface"
import { IFormGroupProps } from "../FormGroup/formgroup.interface"
import { FormStepper } from "../Stepper/FormStepper"
import { IFormResult, IFormTemplateProps } from "./formtemplate.interface"
import { getValidation } from "./validation"
import { useTranslation } from "react-i18next"
import { areDependenciesMet } from "components/Form/FormTemplate/formtemplate.helpers"

interface Sortable {
  sort?: number
}

export const bySortValue = (a: Sortable, b: Sortable) => {
  if (!(a?.sort && b?.sort)) return 0
  return a?.sort > b?.sort ? 1 : -1
}

const FormTemplate: React.FC<IFormTemplateProps> = (props) => {
  const { t } = useTranslation()
  const { id, name, formAreas, apiSubmitEndpoint, formAnswers, translationData, labelKey } = props
  const [formResult, postForm] = useApi() as [IApiResult, IApiFunc]
  const [deviationResult, postDeviation] = useApi() as [IApiResult, IApiFunc]
  const [assignedArea, setAssignedArea] = useState<IFormAreaProps>()
  const [allAreas, setAllAreas] = useState<IFormAreaProps[]>([])
  const [activeStep, setActiveStep] = useState<number>(0)
  const [initialValues, setInitialValues] = useState<FormikValues>({})
  const [validationSteps, setValidationSteps] = useState<any>([])

  useEffect(() => {
    const newValidationSteps = formAreas?.map((area: IFormAreaProps) => {
      const newValidationStep = area.formGroups.flatMap((formGroup: IFormGroupProps) =>
        formGroup.formFields.map((field: IFormFieldProps) => {
          const defaultValue = formAnswers?.find(
            (x) => x.formFieldId.toString() === field.id.toString()
          )?.value
          const newObj = {
            ...initialValues,
            [field.id]: defaultValue ? defaultValue : field.value
          }
          setInitialValues(Object.assign(initialValues, newObj))

          return {
            [field.id]: getValidation(field.dataType, field.optional, field.labelDefault, t, field.labelKey, translationData)
          }
        })
      )

      return Yup.object().shape(Object.assign({}, ...newValidationStep))
    })

    setValidationSteps(newValidationSteps)

    //Om det finns fler än ett steg ska det läggas till ett sista granska steg
    if (formAreas.length > 1) {
      const lastStep = {
        id: formAreas.length,
        labelDefault: "Preview and submit",
        labelKey: "step_label." + "review_and_complete",
        step: formAreas.length,
        stepLabelKey: "step_label." + "review_and_complete",
        stepLabel: "Preview and submit"
      } as IFormAreaProps
      setAllAreas([...formAreas, lastStep])
    } else {
      setAllAreas(formAreas)
    }
  }, [formAnswers, formAreas])

  useEffect(() => {
    const activeStepObject = allAreas?.find((area) => area.step === activeStep)
    setAssignedArea(activeStepObject)
  }, [activeStep, allAreas])

  const handleNext = async (values: any) => {
    const mapValues: Array<IFormResult> = values
      ? Object.entries(values).map(([key, value]) => {
        const mapValue = {
          formFieldId: key,
          value: value?.toString()
        }

        return mapValue
      })
      : []
    if (activeStep === allAreas.length - 1) {
      handleSubmit(mapValues)
    } else {
      handleSubmitFormArea(mapValues)
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }
  }

  const handleSubmitFormArea = async (values: Array<IFormResult>) => {
    const postValues = {
      id: formResult?.data?.id,
      formTemplateId: id,
      status: 0,
      formAnswers: values
    }

    postForm.post(apiEndpoints.form.createorupdateformresult, postValues)
  }

  const handleSubmit = async (values: Array<IFormResult>) => {
    const postValues = {
      id: 0,
      formResult: {
        id: formResult?.data?.id,
        formTemplateId: id,
        status: 0,
        formAnswers: values
      }
    }
    postDeviation.post(apiSubmitEndpoint, postValues)
  }

  return Object.keys(initialValues).length !== 0 ? (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => handleNext(values)}
      enableReinitialize={true}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSteps[activeStep]}>
      {(props) => {
        const { values, handleSubmit, errors } = props

        return (
          <Box>
            <form id="CustomFieldForm" onSubmit={handleSubmit} noValidate>
              <RouterPrompt />
              {deviationResult.status !== 2 ? (
                <Box style={{ maxWidth: "800px" }} mx="auto">
                  {allAreas.length > activeStep && (
                    <FormStepper
                      translationData={translationData}
                      name={name}
                      activeStep={activeStep}
                      areas={allAreas}
                      assignedArea={assignedArea}
                    />
                  )}
                  {activeStep === formAreas.length ? (
                    <>
                      {allAreas.map((area: IFormAreaProps) => {
                        return area?.formGroups?.sort(bySortValue)?.map((formGroup: IFormGroupProps) => (
                          <Box marginBottom={3}>
                            <Typography variant="bodyLarge" gutterBottom>
                              {formGroup.labelKey !== null ? t(`${translationData?.formTypeKey}.${formGroup.labelKey}`, { defaultValue: formGroup.labelDefault }) : formGroup.labelDefault}
                            </Typography>
                            {formGroup.formFields
                              ?.sort(bySortValue)
                              ?.map((field: IFormFieldProps) => {
                                const dependenciesSatisfied = areDependenciesMet(field, area, values)

                                return (
                                  dependenciesSatisfied && (
                                    <FormComponents
                                      initialValues={initialValues}
                                      handleNext={() => handleNext(values)}
                                      field={field}
                                      formikProps={props}
                                      translationData={translationData}
                                    />
                                  )
                                )
                            })}
                          </Box>
                        ))
                      })}
                    </>
                  ) : (
                    assignedArea?.formGroups?.sort(bySortValue)?.map((formGroup: IFormGroupProps) => {
                      const containsCheckboxes = formGroup.formFields.findIndex(
                        (field: IFormFieldProps) => field.component === "Checkbox"
                      ) !== -1
                      const containsRadioButtons = formGroup.formFields.findIndex(
                        (field: IFormFieldProps) => field.component === "RadioButtons"
                      ) !== -1
                      const containsTextFields = formGroup.formFields.filter(
                        (field: IFormFieldProps) => field.component === "TextField"
                      ).length > 1
                      return (
                        <Box
                          marginBottom={3}
                          component={
                            (containsCheckboxes || containsRadioButtons || containsTextFields) &&
                            formGroup.formFields.length > 1
                              ? PaperBox
                              : Box
                          }>
                          <Typography variant="h3" gutterBottom marginBottom="20px">
                            {formGroup.labelKey !== null ? t(`${translationData?.formTypeKey}.${formGroup.labelKey}`, { defaultValue: formGroup.labelDefault }) : formGroup.labelDefault}
                          </Typography>
                          {formGroup.formFields
                            ?.sort(bySortValue)
                            ?.map((field: IFormFieldProps) => {
                              const dependenciesSatisfied = areDependenciesMet(field, assignedArea, values)

                              return (
                                dependenciesSatisfied && (
                                  <FormComponents
                                    initialValues={initialValues}
                                    handleNext={() => handleNext(values)}
                                    field={field}
                                    formikProps={props}
                                    translationData={translationData}
                                  />
                                )
                              )
                            })}
                        </Box>
                      )
                    })
                  )}
                  <Grid container direction="row" gap="2px" justifyContent="center" mt={5} mb={5}>
                    <Grid item>
                      {activeStep !== 0 && (
                        <Button
                          color="primary"
                          variant="text"
                          onClick={handleBack}
                          disabled={activeStep === 0}
                          startIcon={
                            <IconForButton
                              icon={faChevronLeft as IconProp}
                              noPadding={true}
                              color="primary"
                            />
                          }>
                          {globalT(`global.buttons.previous`)}
                        </Button>
                      )}{" "}
                    </Grid>
                    <Grid item>
                      {activeStep === allAreas.length - 1 ? (
                        <ClpButton
                          onClick={handleSubmit}
                          color="success"
                          variant="contained"
                          result={deviationResult}>
                          {globalT(`${translationData?.formTypeKey}.form.button.send_deviation`)}
                        </ClpButton>
                      ) : (
                        <Button
                          endIcon={<IconForButton color="primary" icon={faChevronRight as IconProp} />}
                          color={"primary"}
                          variant="contained"
                          type="submit">
                          {globalT(`global.buttons.next`)}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                deviationResult.status === 2 && (
                  <Grid
                    container
                    direction="row"
                    gap={2}
                    justifyContent="center"
                    alignContent="center"
                    mt={5}
                    mb={5}>
                    <Grid item>
                      <Card sx={{ padding: 3, textAlign: "center" }}>
                        <FontAwesomeIcon
                          icon={faCheck as IconProp}
                          size="5x"
                          color={theme.palette.success.main}
                        />
                        <Typography variant="h1">{t("global.thanks_for_the_help", "Thanks for the help")}!</Typography>
                        <Typography variant="bodyMedium" color="textSecondary">
                          {t(translationData?.formTypeKey + ".form_submitted_success_text", "Your form has been submitted.")}
                        </Typography>
                        <Button
                          sx={{ marginTop: 3 }}
                          variant="contained"
                          component={Link}
                          to={allRoutes.Deviation()}
                          color="primary">
                          {t("global.back_to_overview", "Back to overview")}
                        </Button>
                      </Card>
                    </Grid>
                  </Grid>
                )
              )}
            </form>
          </Box>
        )
      }}
    </Formik>
  ) : (
    <></>
  )
}

export default FormTemplate
